import React, { useState } from "react";

import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { useTranslation } from "next-i18next";
import { AccountApiNS } from "~types/api/account";

import { UserPreview } from "@/components/blocks/global/user-preview/UserPreview";
import { ArrowButton } from "@/components/elements/arrow-button/ArrowButton";
import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";

import ArrowLeftLong from "~public/icons/Arrow Left Long/Arrow Left Long.svg";
import ArrowLeftSmall from "~public/icons/Arrow Left Small/Arrow Left Small.svg";
import ArrowRightLong from "~public/icons/Arrow Right Long/Arrow Right Long.svg";
import ArrowRightSmall from "~public/icons/Arrow Right Small/Arrow Right Small.svg";

import GetUsersListResponse = AccountApiNS.GetUsersListResponse;

interface ChefsResultsProps {
  useQueryResult: UseInfiniteQueryResult<GetUsersListResponse>;
}

export const ChefsResults = ({
  useQueryResult
}: ChefsResultsProps): JSX.Element => {
  const { t } = useTranslation("chefspage");
  const theme = useTheme();
  const isGreaterThanTablet = useMediaQuery(theme.breakpoints.up("md"));

  const {
    fetchNextPage,
    fetchPreviousPage,
    isLoading,
    isFetching,
    data,
    isError
  } = useQueryResult;
  const [currentPage, setCurrentPage] = useState<number>(0);

  const itemCount = isGreaterThanTablet ? 4 : 3;

  const totalPages = Math.ceil(
    data?.pages ? data.pages[0].count / itemCount : 0
  );

  const handleLeftArrowClick = () => {
    if (currentPage < 1) return;

    fetchPreviousPage();
    setCurrentPage(currentPage - 1);
  };
  const handleRightArrowClick = () => {
    if (currentPage === totalPages - 1) return;

    fetchNextPage();
    setCurrentPage(currentPage + 1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexFlow: "column",
        rowGap: 2.5
      }}>
      <Box
        sx={{
          mb: { xs: 2.5, md: 0 },
          height: { xs: 235, md: 400 },
          display: "flex",
          columnGap: { xs: 2, md: 0 },
          justifyContent: "space-evenly",
          "& .spinner": {
            alignSelf: "center !important"
          },
          "& .error": {
            alignSelf: "center !important"
          },
          "&>:nth-of-type(odd)": {
            alignSelf: "end"
          },
          "&>:nth-of-type(even)": {
            alignSelf: "start"
          }
        }}>
        {isFetching && !data?.pages[currentPage] && (
          <CircularProgress className="spinner" />
        )}
        {!isLoading &&
          data?.pages[currentPage] &&
          data?.pages[currentPage].results.map(user => (
            <UserPreview key={user.pk} profile={user} />
          ))}
        {!isLoading &&
          data?.pages[currentPage] &&
          data.pages[currentPage]?.count === 0 && (
            <Box className="error">
              <Typography color={"#a1a1a1"} fontSize={20} fontWeight={500}>
                {"No recipes found"}
              </Typography>
            </Box>
          )}
        {isError && !data?.pages[currentPage] && (
          <Box className="error">
            <Typography color={"#a1a1a1"} fontSize={20} fontWeight={500}>
              {t("errors:default.went-wrong")}
            </Typography>
          </Box>
        )}
      </Box>
      {!isError && (
        <Box
          sx={{
            height: 36,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 4
          }}>
          <Typography
            fontWeight={400}
            sx={{ ml: 0, mr: "auto", fontSize: { xs: 16, md: 24 } }}>
            {currentPage + 1}
            <Box component={"span"} color="#D0D5DD">
              {" /"}
              {isLoading ? <CircularProgress size={14} /> : totalPages}
            </Box>
          </Typography>
          <ArrowButton
            onClick={handleLeftArrowClick}
            disabled={isLoading || currentPage === 0}
            icon={isGreaterThanTablet ? ArrowLeftLong : ArrowLeftSmall}
          />
          <ArrowButton
            onClick={handleRightArrowClick}
            disabled={isLoading || currentPage === totalPages - 1}
            icon={isGreaterThanTablet ? ArrowRightLong : ArrowRightSmall}
          />
        </Box>
      )}
    </Box>
  );
};
